import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDay } from "@/lib/format";
import { MONTHS, TIMEZONES } from "@/lib/people";
import { toast } from "sonner";

const err = (e) => toast.error(apiError(e.response?.data?.detail));

function GeneralSettings({ orgId, s, approvers, onSaved }) {
  const [f, setF] = useState({ leave_year_start_month: s.leave_year_start_month, timezone: s.timezone, alternate_approver_id: s.alternate_approver_id || "none" });
  const save = async () => {
    try {
      await api.put(`/workspace/leave/settings?org_id=${orgId}`, { ...f, alternate_approver_id: f.alternate_approver_id === "none" ? "" : f.alternate_approver_id });
      toast.success("Settings saved"); onSaved();
    } catch (e) { err(e); }
  };
  const zones = TIMEZONES.includes(s.timezone) ? TIMEZONES : [s.timezone, ...TIMEZONES];
  return (
    <div className="amden-card p-5" data-testid="leave-general-settings">
      <h3 className="mb-3 font-display text-sm font-semibold text-[#0B1729]">Company rules</h3>
      <div className="grid gap-3 md:grid-cols-3">
        <div><Label>Leave year starts</Label>
          <Select value={String(f.leave_year_start_month)} onValueChange={(v) => setF({ ...f, leave_year_start_month: Number(v) })}>
            <SelectTrigger className="mt-1" data-testid="settings-year-start"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>1 {m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Time zone</Label>
          <Select value={f.timezone} onValueChange={(v) => setF({ ...f, timezone: v })}>
            <SelectTrigger className="mt-1" data-testid="settings-timezone"><SelectValue /></SelectTrigger>
            <SelectContent>{zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Alternate approver</Label>
          <Select value={f.alternate_approver_id} onValueChange={(v) => setF({ ...f, alternate_approver_id: v })}>
            <SelectTrigger className="mt-1" data-testid="settings-alt-approver"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not set</SelectItem>
              {approvers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className="mt-2 text-xs text-[#1E2D44]/50">The alternate approver handles requests from people without a manager — and from managers themselves. Nobody approves their own leave or time.</p>
      <Button size="sm" onClick={save} className="mt-3 bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="settings-save-btn">Save rules</Button>
    </div>
  );
}

function LeaveTypes({ orgId, types, onSaved }) {
  const [name, setName] = useState("");
  const patch = async (t, body) => { try { await api.patch(`/workspace/leave/types/${t.id}`, body); onSaved(); } catch (e) { err(e); } };
  const add = async () => {
    try { await api.post(`/workspace/leave/types?org_id=${orgId}`, { name, tracks_balance: false, requires_approval: true, sensitive: false }); setName(""); toast.success("Leave type added"); onSaved(); }
    catch (e) { err(e); }
  };
  const flags = [["tracks_balance", "Balance"], ["requires_approval", "Approval"], ["sensitive", "Sensitive"], ["active", "Active"]];
  return (
    <div className="amden-card p-5" data-testid="leave-types-settings">
      <h3 className="mb-1 font-display text-sm font-semibold text-[#0B1729]">Leave types</h3>
      <p className="mb-3 text-xs text-[#1E2D44]/50">Sensitive types show as "Absent" to managers and are approved by a people administrator.</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wide text-[#1E2D44]/45"><th className="py-2">Type</th>{flags.map(([, l]) => <th key={l} className="py-2 text-center">{l}</th>)}</tr></thead>
          <tbody>
            {types.map((t) => (
              <tr key={t.id} className="border-t border-[#EEF1F6]" data-testid={`leave-type-row-${t.id}`}>
                <td className="py-2"><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ background: t.colour }} />{t.name}</td>
                {flags.map(([k]) => (
                  <td key={k} className="py-2 text-center"><Switch checked={!!t[k]} onCheckedChange={(v) => patch(t, { [k]: v })} data-testid={`type-${t.id}-${k}`} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New leave type, e.g. Study leave" className="h-9" data-testid="new-leave-type-name" />
        <Button size="sm" disabled={!name.trim()} onClick={add} data-testid="add-leave-type-btn">Add</Button>
      </div>
    </div>
  );
}

function Holidays({ orgId, holidays, today, onSaved }) {
  const [f, setF] = useState({ date: "", name: "" });
  const remove = async (h) => { try { await api.delete(`/workspace/leave/holidays/${h.id}`); toast.success("Public holiday removed"); onSaved(); } catch (e) { err(e); } };
  const add = async () => {
    try { await api.post(`/workspace/leave/holidays?org_id=${orgId}`, f); setF({ date: "", name: "" }); toast.success("Public holiday added"); onSaved(); }
    catch (e) { err(e); }
  };
  const shown = holidays.filter((h) => h.date >= `${today.slice(0, 4)}-01-01`);
  return (
    <div className="amden-card p-5" data-testid="holidays-settings">
      <h3 className="mb-1 font-display text-sm font-semibold text-[#0B1729]">Public holidays</h3>
      <p className="mb-3 text-xs text-[#1E2D44]/50">Started with the Irish public holidays — edit them to match your company. These days are never deducted from leave.</p>
      <ul className="mb-3 grid gap-1.5 sm:grid-cols-2">
        {shown.map((h) => (
          <li key={h.id} className={`flex items-center justify-between gap-2 rounded-lg border border-[#EEF1F6] px-3 py-2 text-sm ${h.date < today ? "opacity-50" : ""}`} data-testid={`holiday-${h.date}`}>
            <span><span className="font-medium text-[#0B1729]">{h.name}</span> <span className="text-xs text-[#1E2D44]/50">{fmtDay(h.date)} {h.date.slice(0, 4)}</span></span>
            <button onClick={() => remove(h)} className="text-[#1E2D44]/35 hover:text-[#C95555]" data-testid={`remove-holiday-${h.date}`}><Icons.X size={14} /></button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="h-9 w-40" data-testid="new-holiday-date" />
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className="h-9 flex-1" data-testid="new-holiday-name" />
        <Button size="sm" disabled={!f.date || !f.name.trim()} onClick={add} data-testid="add-holiday-btn">Add</Button>
      </div>
    </div>
  );
}

export function LeaveSettingsPanel({ orgId, members }) {
  const [s, setS] = useState(null);
  const load = () => api.get(`/workspace/leave/settings?org_id=${orgId}`).then((r) => setS(r.data));
  useEffect(() => { load(); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return <Icons.Loader2 className="mx-auto mt-8 animate-spin text-[#FFB000]" />;
  const approvers = members.filter((m) => ["client_admin", "client_manager"].includes(m.role) || m.people_admin);
  return (
    <div className="space-y-4" data-testid="leave-settings-panel">
      <GeneralSettings orgId={orgId} s={s} approvers={approvers} onSaved={load} />
      <LeaveTypes orgId={orgId} types={s.types} onSaved={load} />
      <Holidays orgId={orgId} holidays={s.holidays} today={s.today} onSaved={load} />
    </div>
  );
}
